import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

// Poll interval for sidebar badges. Keep it low-chatter — but responsive enough
// that newly-assigned tasks show up quickly. We also refresh when the browser
// tab regains focus and when any component broadcasts a `whitenton:tasks-changed`
// event (fired after create/assign/complete calls).
const POLL_MS = 30_000;
const ACCOUNTING_ROLES = ["admin", "accounting"];
const EMPTY_COUNTS = { tasks: 0, reports: 0, deadlines: 0, accounting: 0, accountingAlerts: 0, conversations: null, completionNotifications: null };

/**
 * Returns actionable counts plus the conversation feed for task-message alerts.
 * - tasks: non-completed work assigned to me that appears on the Tasks page
 *   (misc tasks + report assignments), plus one alert per unread conversation
 * - reports: non-completed REPORT assignments assigned to me
 * - deadlines: any of my tasks/reports with a deadline within 7 days and not completed
 * - accounting: invoice reviews with a reviewer response awaiting Accounting
 *   (only fetched for accounting / admin roles)
 * - accountingAlerts: completed preliminary reviews and report submittals that
 *   Accounting has not cleared yet
 */
export default function useSidebarBadges() {
  const { user } = useAuth();
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const requestSequence = useRef(0);
  const userId = user?.id;
  const canSeeAccounting = ACCOUNTING_ROLES.includes(user?.role);

  const load = useCallback(async () => {
    if (!userId) return;
    const sequence = ++requestSequence.current;
    try {
      const tasksReq = api.get("/my-tasks", { params: { scope: "to_me" } });
      const acctReq = canSeeAccounting ? api.get("/accounting/invoices", { params: { view: "active" } }) : Promise.resolve(null);
      const alertReq = canSeeAccounting ? api.get("/accounting-alerts/count") : Promise.resolve(null);
      const conversationsReq = api.get("/my-tasks", { params: { scope: "conversations" } }).catch(() => null);
      const completionsReq = api.get("/notifications/completion-alerts").catch(() => null);
      const [tasksRes, acctRes, alertRes, conversationsRes, completionRes] = await Promise.all([tasksReq, acctReq, alertReq, conversationsReq, completionsReq]);
      if (sequence !== requestSequence.current) return;
      const items = Array.isArray(tasksRes.data) ? tasksRes.data : [];
      const now = Date.now();
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      let tasks = 0, reports = 0, deadlines = 0;
      for (const t of items) {
        const status = t.status || "pending";
        if (status === "completed") continue;
        if (t.type === "report") reports += 1;
        else tasks += 1;
        if (t.deadline) {
          const ts = Date.parse(t.deadline);
          if (!Number.isNaN(ts) && ts - now <= sevenDays) deadlines += 1;
        }
      }
      let accounting = 0;
      if (acctRes?.data?.counts) accounting = acctRes.data.counts.response || 0;
      const accountingAlerts = Number(alertRes?.data?.count) || 0;
      setCounts((previous) => {
        const conversations = Array.isArray(conversationsRes?.data)
          ? [...new Map(conversationsRes.data.map((task) => [`${task.type}:${task.project_id || ""}:${task.id}`, task])).values()]
          : previous.conversations;
        // A reply needs its own alert even when its assignment is already counted.
        const unreadCount = (conversations || []).filter((task) => task.conversation_unread_count > 0).length;
        const completionNotifications = completionRes?.data || previous.completionNotifications;
        const completionCount = Number(completionNotifications?.count) || 0;
        return { tasks: tasks + reports + unreadCount + completionCount, reports, deadlines, accounting, accountingAlerts, conversations, completionNotifications };
      });
    } catch {
      /* leave last known counts in place */
    }
  }, [canSeeAccounting, userId]);

  useEffect(() => {
    setCounts(EMPTY_COUNTS);
    load();
    const h = setInterval(load, POLL_MS);
    const onFocus = () => load();
    const onVisibility = () => { if (document.visibilityState === "visible") load(); };
    const onChanged = () => load();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("whitenton:tasks-changed", onChanged);
    window.addEventListener("whitenton:accounting-changed", onChanged);
    window.addEventListener("whitenton:accounting-alerts-changed", onChanged);
    return () => {
      requestSequence.current += 1;
      clearInterval(h);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("whitenton:tasks-changed", onChanged);
      window.removeEventListener("whitenton:accounting-changed", onChanged);
      window.removeEventListener("whitenton:accounting-alerts-changed", onChanged);
    };
  }, [load]);

  return counts;
}
